import {
  SetDictionary, SpellingDictionary, UnionDictionary,
  type Dictionary, type SpellingData, type SpellingMode, type WordListId,
} from '@nt2/engine';
import { decodePack, type PackMeta } from './pack.js';

/**
 * Word packs are fetched once and cached for the tab's lifetime. A match must
 * never stall on the dictionary mid-play, so the pack is loaded during the
 * lobby and the match simply cannot be started until it has arrived.
 */
const cache = new Map<string, { dict: Dictionary; meta: PackMeta }>();
let manifest: PackMeta[] | null = null;

export async function loadManifest(): Promise<PackMeta[]> {
  if (manifest) return manifest;
  const res = await fetch('dict/manifest.json');
  if (!res.ok) throw new Error(`Word list manifest unavailable (HTTP ${res.status}).`);
  manifest = (await res.json()) as PackMeta[];
  return manifest;
}

export async function loadPack(id: string): Promise<{ dict: Dictionary; meta: PackMeta }> {
  const hit = cache.get(id);
  if (hit) return hit;
  const res = await fetch(`dict/${id}.pack`);
  if (!res.ok) throw new Error(`Could not load the "${id}" word list (HTTP ${res.status}).`);
  const { meta, words } = decodePack(await res.text());
  const entry = {
    meta,
    dict: new SetDictionary(id as WordListId, meta.name, words) as Dictionary,
  };
  cache.set(id, entry);
  return entry;
}

/**
 * Build the dictionary a match will play under.
 *
 * `family` deliberately unions with nothing: the point of the gentle list is
 * that the obscure words are absent, so folding a bigger list back in would
 * defeat it.
 */
export async function dictionaryFor(id: WordListId, spelling: SpellingMode = 'both'): Promise<Dictionary> {
  let base: Dictionary;
  if (id === 'custom') {
    const [a, b] = await Promise.all([loadPack('enable'), loadPack('wordnik')]);
    base = new UnionDictionary('custom', 'ENABLE + Wordnik', [a.dict, b.dict]);
  } else {
    base = (await loadPack(id)).dict;
  }
  const data = await loadSpelling();
  // Without the spelling file the list is used as it is: a word list that
  // works beats a match that cannot start.
  return data ? new SpellingDictionary(base, data, spelling) : base;
}

let spellingData: Promise<SpellingData | null> | null = null;
/** The British/American spelling pairs, fetched once. */
function loadSpelling(): Promise<SpellingData | null> {
  spellingData ??= fetch('dict/spelling.json')
    .then((r) => (r.ok ? r.json() as Promise<SpellingData> : null))
    .catch(() => null);
  return spellingData;
}

// ---------------------------------------------------------------------------
// Definitions
// ---------------------------------------------------------------------------

export interface Definition { word: string; partOfSpeech: string; text: string; }

const defCache = new Map<string, Definition[] | null>();
const DEF_ENDPOINT = 'https://api.dictionaryapi.dev/api/v2/entries/en/';
/** Short on purpose: this is a garnish on a timed game, not a dependency. */
const DEF_TIMEOUT_MS = 3500;

/**
 * Fetch a short definition for a word the player has spent a check on.
 *
 * Validity never depends on this: the bundled list already decided, and a
 * failed or slow lookup just means the player sees the verdict without the
 * gloss. That keeps the game fully playable offline and keeps a third party
 * off the critical path of the rules.
 */
export async function lookupDefinition(
  word: string,
  timeoutMs = DEF_TIMEOUT_MS,
): Promise<Definition[] | null> {
  const w = word.toLowerCase();
  if (defCache.has(w)) return defCache.get(w) ?? null;
  // A blocked or throttled network can leave a plain fetch pending for half a
  // minute. The player is mid-race and the definition is a nicety, so the
  // lookup gets a short leash and the panel resolves either way.
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), timeoutMs);
  try {
    const res = await fetch(DEF_ENDPOINT + encodeURIComponent(w), { signal: abort.signal });
    if (!res.ok) { defCache.set(w, null); return null; }
    const data = (await res.json()) as Array<{
      word: string;
      meanings?: Array<{ partOfSpeech?: string; definitions?: Array<{ definition?: string }> }>;
    }>;
    const out: Definition[] = [];
    for (const entry of data.slice(0, 2)) {
      for (const m of entry.meanings?.slice(0, 2) ?? []) {
        const text = m.definitions?.[0]?.definition;
        if (text) {
          out.push({ word: entry.word, partOfSpeech: m.partOfSpeech ?? '', text });
        }
      }
    }
    const result = out.length ? out.slice(0, 3) : null;
    defCache.set(w, result);
    return result;
  } catch {
    // Offline, blocked, timed out, or the endpoint is down. Not an error the
    // player should be shown as a failure — the check itself still worked, and
    // validity never depended on this.
    defCache.set(w, null);
    return null;
  } finally {
    clearTimeout(timer);
  }
}
